import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle, XCircle, MessageCircle, Shield, Star, Send, Dog, Inbox } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/lib/api';

const firstNameOnly = (value) => (value || '').trim().split(/\s+/)[0] || 'Cuidador';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getPhotoUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${API_URL}${path}`;
};

const SERVICE_LABELS = {
  paseo: 'Paseo',
  cuidado: 'Cuidado',
  daycare: 'Daycare'
};

const ClientProposals = () => {
  const [loading, setLoading] = useState(true);
  const [proposals, setProposals] = useState([]);

  useEffect(() => {
    loadProposals();
  }, []);

  const loadProposals = async () => {
    try {
      const res = await api.get('/proposals/received');
      setProposals(res.data || []);
    } catch (e) {
      toast.error('No pudimos cargar tus propuestas');
    } finally {
      setLoading(false);
    }
  };

  const respondProposal = async (proposalId, status) => {
    try {
      await api.put(`/proposals/${proposalId}/respond`, { status });
      setProposals(prev => prev.map(p => {
        if (p.proposal_id === proposalId) return { ...p, status };
        if (status === 'accepted' && p.status === 'pending' && p.care_request_id === proposals.find(x => x.proposal_id === proposalId)?.care_request_id) {
          return { ...p, status: 'rejected' };
        }
        return p;
      }));
      toast.success(status === 'accepted' ? 'Propuesta aceptada' : 'Propuesta descartada');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al responder');
    }
  };

  const pending = proposals.filter(p => p.status === 'pending').length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-16 h-16 border-4 border-[#E6202E] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="client-proposals-page">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="mb-6">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#E6202E]">
            <ArrowLeft className="w-4 h-4" />
            Volver al panel
          </Link>
        </div>

        <div className="bg-white rounded-3xl shadow-sm border p-6 md:p-8 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
            <div>
              <p className="text-sm font-semibold text-[#E6202E] mb-2">Centro de propuestas</p>
              <h1 className="text-3xl md:text-4xl font-black text-gray-900">Propuestas recibidas</h1>
              <p className="text-gray-500 mt-2">
                Revisa los cuidadores interesados, compara opciones y elige con quién avanzar.
              </p>
            </div>

            <div className="rounded-2xl bg-red-50 border border-red-100 p-4 min-w-[170px]">
              <p className="text-3xl font-black text-gray-900">{pending}</p>
              <p className="text-sm font-bold text-gray-800">Pendientes</p>
              <p className="text-xs text-gray-500">Por responder</p>
            </div>
          </div>
        </div>

        {proposals.length === 0 ? (
          <div className="bg-white rounded-3xl shadow-sm border p-10 text-center">
            <Inbox className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900">Aún no tienes propuestas</h2>
            <p className="text-gray-500 mt-2 mb-5">Cuando un cuidador postule a tus solicitudes, aparecerá acá.</p>
            <Link to="/dashboard">
              <Button className="bg-[#E6202E] hover:bg-[#D31522]">Ver mis solicitudes</Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {proposals.map((p) => {
              const isPending = p.status === 'pending';
              const isAccepted = p.status === 'accepted';
              const isRejected = p.status === 'rejected';

              return (
                <div
                  key={p.proposal_id}
                  className={`bg-white rounded-3xl shadow-sm border p-5 md:p-6 ${
                    isAccepted ? 'border-green-200 bg-green-50/40' :
                    isRejected ? 'opacity-70' :
                    'hover:border-[#E6202E]/40'
                  }`}
                  data-testid={`proposal-card-${p.proposal_id}`}
                >
                  <div className="flex flex-col md:flex-row gap-5">
                    <div className="flex gap-4 flex-1">
                      <div className="w-16 h-16 rounded-2xl overflow-hidden bg-gray-100 flex-shrink-0">
                        {p.provider_photo ? (
                          <img src={getPhotoUrl(p.provider_photo)} alt={firstNameOnly(p.provider_business_name)} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-xl font-black text-gray-400">
                            {firstNameOnly(p.provider_business_name)?.[0] || 'C'}
                          </div>
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Link to={`/provider/${p.provider_provider_id}`} className="text-lg font-black text-gray-900 hover:text-[#E6202E]">
                            {firstNameOnly(p.provider_business_name)}
                          </Link>
                          {p.provider_verified && <Shield className="w-4 h-4 text-green-600" />}
                          {p.provider_rating && (
                            <span className="inline-flex items-center gap-1 text-xs font-bold text-yellow-700 bg-yellow-50 px-2 py-1 rounded-full">
                              <Star className="w-3 h-3 fill-yellow-400" />
                              {p.provider_rating.toFixed(1)}
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          <span className="px-2.5 py-1 text-xs font-bold bg-red-50 text-[#E6202E] rounded-full">
                            {SERVICE_LABELS[p.service_type] || p.service_type || 'Servicio'}
                          </span>
                          <span className={`px-2.5 py-1 text-xs font-bold rounded-full ${
                            isAccepted ? 'bg-green-100 text-green-700' :
                            isRejected ? 'bg-gray-200 text-gray-600' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {isAccepted ? 'Aceptada' : isRejected ? 'Descartada' : 'Pendiente'}
                          </span>
                        </div>

                        <p className="text-sm text-gray-700 mt-3">{p.message || 'Estoy interesado en tu solicitud.'}</p>

                        <div className="flex items-center gap-2 text-xs text-gray-400 mt-3">
                          <Dog className="w-3 h-3" />
                          <span>{p.pet_name || 'Tu mascota'}</span>
                          {p.created_at && <span>• {new Date(p.created_at).toLocaleDateString('es-CL')}</span>}
                        </div>
                      </div>
                    </div>

                    <div className="md:w-[230px] flex md:flex-col gap-2">
                      <Link to={`/provider/${p.provider_provider_id}`} className="flex-1">
                        <Button variant="outline" className="w-full rounded-xl">
                          Ver perfil
                        </Button>
                      </Link>

                      {isAccepted ? (
                        <>
                          <Link to="/historial?tab=en-curso" className="flex-1">
                            <Button className="w-full bg-green-600 hover:bg-green-700 rounded-xl">
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Ver servicio en curso
                            </Button>
                          </Link>

                          <Link to="/chat" className="flex-1">
                            <Button variant="outline" className="w-full rounded-xl">
                              <MessageCircle className="w-4 h-4 mr-2" />
                              Abrir chat
                            </Button>
                          </Link>
                        </>
                      ) : (
                        <div className="flex-1 rounded-xl bg-gray-50 border px-3 py-2 text-sm text-gray-500 text-center">
                          Chat disponible al aceptar
                        </div>
                      )}

                      {isPending && (
                        <>
                          <Button
                            onClick={() => respondProposal(p.proposal_id, 'accepted')}
                            className="flex-1 bg-green-600 hover:bg-green-700 rounded-xl"
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Aceptar
                          </Button>
                          <Button
                            onClick={() => respondProposal(p.proposal_id, 'rejected')}
                            variant="outline"
                            className="flex-1 text-red-600 border-red-200 hover:bg-red-50 rounded-xl"
                          >
                            <XCircle className="w-4 h-4 mr-2" />
                            Descartar
                          </Button>
                        </>
                      )}

                      {isAccepted && (
                        <div className="rounded-xl bg-green-50 border border-green-200 text-green-800 px-3 py-3 text-sm text-center">
                          <p className="font-bold">Cuidador elegido</p>
                          <p className="text-xs mt-1">
                            Coordina los detalles y revisa tu servicio en curso.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-6 text-center">
          <Link to="/dashboard">
            <Button variant="outline" className="rounded-xl">
              <Send className="w-4 h-4 mr-2" />
              Ver mis solicitudes
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ClientProposals;
